import type { HomeIndex, WorkspacePageProps } from '@/types';
import { Badge } from '@astryxdesign/core/Badge';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { usePage } from '@inertiajs/react';
import { CalendarDays, ClipboardList, FolderKanban, Rows3, Users } from 'lucide-react';

type Props = WorkspacePageProps & HomeIndex;

interface OverviewMetric {
  label: string;
  value: string;
  detail: string;
  tone: 'accent' | 'sand' | 'teal';
}

const overviewMetrics: OverviewMetric[] = [
  {
    label: 'Partes de hoy',
    value: '0',
    detail: 'Esperando las primeras cargas del equipo.',
    tone: 'accent',
  },
  {
    label: 'Proyectos activos',
    value: '0',
    detail: 'Van a aparecer cuando el equipo informe actividad.',
    tone: 'sand',
  },
  {
    label: 'Bloqueos',
    value: '0',
    detail: 'Los bloqueos destacados quedan visibles para seguimiento.',
    tone: 'teal',
  },
];

const workflowRows = [
  'Cada persona carga proyecto y actividades.',
  'El equipo ve avances, pendientes y bloqueos.',
  'Los leads escanean el cierre del día sin pedir reportes extra.',
];

const Index = () => {
  const { auth } = usePage<Props>().props;

  return (
    <VStack gap={6} width="100%" className="iorio-home-page">
      <section className="iorio-home-hero">
        <VStack gap={4} className="iorio-home-hero-copy">
          <HStack gap={2} wrap="wrap" vAlign="center">
            <Badge label={auth.membership.role} variant="red" />
            <Badge label="Parte diario" variant="teal" />
          </HStack>

          <VStack gap={2}>
            <Heading level={1}>Hoy en {auth.team.name}</Heading>
            <Text type="large" color="secondary">
              {auth.user.displayName}, este es el punto de partida para ordenar el standup del
              equipo y ver quién avanzó en qué proyecto.
            </Text>
          </VStack>
        </VStack>

        <Card padding={6} className="iorio-home-daily-panel">
          <VStack gap={5}>
            <HStack hAlign="between" vAlign="start" gap={3}>
              <VStack gap={1}>
                <Text type="supporting" weight="bold" className="iorio-home-eyebrow">
                  Vista de hoy
                </Text>
                <Heading level={2}>Todavía no hay partes cargados</Heading>
              </VStack>
              <span className="iorio-home-icon" aria-hidden="true">
                <Icon icon={CalendarDays} size="sm" />
              </span>
            </HStack>

            <Text type="body" color="secondary">
              Cuando el equipo empiece a cargar, acá se van a agrupar proyecto, actividades y
              bloqueos para leer el día de un vistazo.
            </Text>

            <VStack gap={2} className="iorio-home-report-preview">
              <HStack hAlign="between" vAlign="center" gap={3} className="iorio-home-report-row">
                <HStack gap={2} vAlign="center">
                  <Icon icon={ClipboardList} size="sm" />
                  <Text type="body" weight="bold">
                    Tu parte
                  </Text>
                </HStack>
                <Badge label="Pendiente" variant="warning" />
              </HStack>

              <HStack hAlign="between" vAlign="center" gap={3} className="iorio-home-report-row">
                <HStack gap={2} vAlign="center">
                  <Icon icon={Users} size="sm" />
                  <Text type="body" weight="bold">
                    Equipo
                  </Text>
                </HStack>
                <Text type="supporting" color="secondary">
                  0 cargados
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </Card>
      </section>

      <section className="iorio-home-metrics" aria-label="Resumen diario">
        {overviewMetrics.map((metric) => (
          <Card
            key={metric.label}
            padding={5}
            className={`iorio-home-metric iorio-home-metric-${metric.tone}`}
          >
            <VStack gap={3}>
              <Text type="supporting" weight="bold" className="iorio-home-eyebrow">
                {metric.label}
              </Text>
              <Text type="body" weight="bold" className="iorio-home-metric-value">
                {metric.value}
              </Text>
              <Text type="supporting" color="secondary">
                {metric.detail}
              </Text>
            </VStack>
          </Card>
        ))}
      </section>

      <section className="iorio-home-workflow">
        <Card padding={6} className="iorio-home-panel">
          <VStack gap={4}>
            <HStack gap={2} vAlign="center">
              <span className="iorio-home-icon iorio-home-icon-muted" aria-hidden="true">
                <Icon icon={Rows3} size="sm" />
              </span>
              <Heading level={3}>Cómo se va a leer el día</Heading>
            </HStack>

            <VStack gap={3}>
              {workflowRows.map((row, index) => (
                <HStack key={row} gap={3} vAlign="start" className="iorio-home-step">
                  <span className="iorio-home-step-number">{index + 1}</span>
                  <Text type="body">{row}</Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        </Card>

        <Card padding={6} variant="muted" className="iorio-home-panel iorio-home-project-panel">
          <VStack gap={4}>
            <HStack gap={2} vAlign="center">
              <span className="iorio-home-icon iorio-home-icon-muted" aria-hidden="true">
                <Icon icon={FolderKanban} size="sm" />
              </span>
              <Heading level={3}>Proyectos</Heading>
            </HStack>

            <VStack gap={2}>
              <Text type="body" weight="bold">
                Sin actividad informada todavía
              </Text>
              <Text type="supporting" color="secondary">
                Los proyectos van a tomar presencia cuando aparezcan en los partes diarios.
              </Text>
            </VStack>
          </VStack>
        </Card>
      </section>
    </VStack>
  );
};

export default Index;
